// Angular imports
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';

// 3rd party imports
import {
  DxPopupComponent,
  DxValidationGroupComponent
} from 'devextreme-angular';

// Service imports
import { AuthService } from '../../_services/auth.service';
import { ScreenService } from '../../_services/shared-services/screen.service';
import { UserService } from '../../_services/data-services/user.service';
import { HelperService } from '../../_services/shared-services/helper.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';

// Model imports
import { AccountParameterDto } from '../../_models/account-parameter-dto';

// Constant imports
import { ImageConstants } from '../../_constants/image-constants';
import { ValidationFormatConstants } from '../../_constants/validation-format-constants';

// CC-Common imports
import { RxjsUtils } from '@therealmanage/ciranet-ui-common';

@Component({
  selector: 'app-add-property-modal',
  templateUrl: './add-property-modal.component.html',
  styleUrls: ['./add-property-modal.component.scss'],
  standalone: false
})
export class AddPropertyModalComponent implements OnInit {
  @ViewChild('addPropertyPopup', { static: false })
  addPropertyPopup: DxPopupComponent;

  @ViewChild('addPropertyValidationGroup', { static: false })
  addPropertyValidationGroup: DxValidationGroupComponent;

  @Output() onPropertyAdded: EventEmitter<void> = new EventEmitter();

  isSmallScreen: boolean = false;
  popupVisible: boolean = false;
  popupWidth: string | number = '100%';
  popupPosition: any = { my: 'top', at: 'top', of: window };

  loadingVisible: boolean = false;
  accountParameterDto: AccountParameterDto = new AccountParameterDto();
  accountNumberPattern: any = ValidationFormatConstants.accountNumberPattern;
  propertyAdded: boolean = false;

  // Help popup
  isHelpShow: boolean = false;
  helpImage: string;

  constructor(
    private authService: AuthService,
    private screenService: ScreenService,
    private userService: UserService,
    private helperService: HelperService,
    private serverErrorService: ServerErrorService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.helpImage = ImageConstants.helpAccountImage;
    this.updateScreenSize();
    RxjsUtils.safeSubscribe(this.screenService.changed, () =>
      this.updateScreenSize()
    );
  }

  show(): void {
    // Close the sidebar before showing the popup
    this.helperService.subjectCloseSideNav.next();

    // Reset form data and state
    this.accountParameterDto = new AccountParameterDto();
    this.propertyAdded = false;
    this.updatePopupPosition();
    document.body.classList.add('add-property-modal-open');

    // Use setTimeout to defer state change to next tick, avoiding
    // ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.popupVisible = true;
      this.cdr.markForCheck();
    });
  }

  hide(): void {
    document.body.classList.remove('add-property-modal-open');
    this.popupVisible = false;
  }

  onPopupShown(): void {
    this.addPropertyPopup.instance.repaint();
  }

  checkSelection(e: any): void {
    // Prevent closing if no property has been added
    e.cancel = !this.propertyAdded;
  }

  onHelpClick(): void {
    this.isHelpShow = true;
  }

  addProperty(): void {
    const isValid = this.addPropertyValidationGroup.instance.validate().isValid;
    if (isValid) {
      this.loadingVisible = true;
      this.accountParameterDto.customerID =
        this.accountParameterDto.customerID.toUpperCase();

      RxjsUtils.safeSubscribe(
        this.userService.addUserProperty(this.accountParameterDto),
        (res) => {
          this.loadingVisible = false;
          if (!!res.isSuccess) {
            // Mark property as added so popup can close
            this.propertyAdded = true;

            // Close popup and emit events
            this.popupVisible = false;
            this.userService.updateSubjectNewPropertyAdded();
            this.onPropertyAdded.emit();
          }
        },
        (error) => {
          this.loadingVisible = false;
          this.serverErrorService.displayError(error);
        }
      );
    }
  }

  private updatePopupPosition(): void {
    const contentWrapper = document.querySelector('.content-wrapper');
    const headerHeight = contentWrapper
      ? contentWrapper.getBoundingClientRect().top
      : 56;

    // Check if sidebar is visible (collapsed sidebar is ~60px)
    const sidebar = document.querySelector('.sidebar');
    const sidebarVisible = sidebar && sidebar.clientWidth > 0;
    const sidebarWidth = sidebarVisible ? sidebar?.clientWidth || 60 : 0;

    if (this.isSmallScreen && !sidebarVisible) {
      // Mobile with no sidebar - full width
      this.popupWidth = '100vw';
      this.popupPosition = {
        my: 'top left',
        at: 'top left',
        of: window,
        offset: `0 ${headerHeight}`
      };
    } else {
      // Desktop or mobile with sidebar visible - account for sidebar
      const offsetWidth = sidebarWidth > 0 ? sidebarWidth : 60;
      this.popupWidth = `calc(100vw - ${offsetWidth}px)`;
      this.popupPosition = {
        my: 'top left',
        at: 'top left',
        of: window,
        offset: `${offsetWidth} ${headerHeight}`
      };
    }
  }

  private updateScreenSize(): void {
    this.isSmallScreen = this.screenService.isSmallScreen();
    if (this.popupVisible) {
      this.updatePopupPosition();
    }
  }

  logout(): void {
    this.authService.logOut();
    this.authService.updateSubjectLogout(true);
    this.authService.updateSubjectLoginSuccess(false);
  }
}
